/**
 * ProviderOrders — the hardware provider's order book: hardware purchased
 * through the site by software providers. List + expandable detail (lines,
 * shipping) and the fulfilment action (mark a paid order shipped).
 *
 * Used on the seller's Orders page (left nav) and the super-admin Orders tab
 * on HardwareProviderDetail (linkThrough adds buyer links).
 */

import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from '@emotion/styled';
import {
  getManufacturerOrders,
  getManufacturerOrder,
  shipManufacturerOrder,
  registerOrderDevices,
  completeManufacturerOrder,
  acceptTermsOrder,
  declineTermsOrder,
  OrderStatus,
} from '../services/orders';
import { formatMoney } from '../services/deviceBoards';
import { formatWhen } from './BoardPriceModal';
import {
  TableContainer,
  StyledTable,
  Th,
  Td,
  StatusChip,
  LoadingText,
  EmptyState,
  MutedText,
  Caption,
  Row,
  Select,
  Input,
  TextArea,
  Button,
  ErrorAlert,
  HelperText,
} from './styled';

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin: ${({ theme }) => theme.spacing(6)} 0 ${({ theme }) => theme.spacing(3)};
`;

const ProviderLink = styled(Link)`
  color: ${({ theme }) => theme.accent.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.font.weight.medium};

  &:hover { text-decoration: underline; }
`;

const OrderRow = styled.tr`
  cursor: pointer;

  &:hover td {
    background: ${({ theme }) => theme.background.secondary};
  }
`;

const ExpandTd = styled(Td)`
  background: ${({ theme }) => theme.background.secondary};
`;

const DetailColumns = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(8)};
  flex-wrap: wrap;
  padding: ${({ theme }) => theme.spacing(2)} 0;
`;

const ShipForm = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

function statusVariant(status: OrderStatus): 'warning' | 'blue' | 'positive' | 'green' | 'neutral' {
  switch (status) {
    case 'pending':   return 'warning';
    case 'paid':      return 'blue';
    case 'shipped':   return 'positive';
    case 'completed': return 'green';
    default:          return 'neutral';
  }
}

function OrderDetail({ manufacturerId, orderId, linkThrough }: {
  manufacturerId: number;
  orderId: number;
  linkThrough: boolean;
}) {
  const queryClient = useQueryClient();
  const [carrier, setCarrier] = useState('');
  const [tracking, setTracking] = useState('');
  const [shipError, setShipError] = useState('');

  const { data: order, isLoading } = useQuery({
    queryKey: ['manufacturer-order', manufacturerId, orderId],
    queryFn: () => getManufacturerOrder(manufacturerId, orderId),
  });

  const [deviceIdsText, setDeviceIdsText] = useState('');
  const [registerError, setRegisterError] = useState<string[]>([]);

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['manufacturer-order', manufacturerId, orderId] } as any);
    queryClient.invalidateQueries({ queryKey: ['manufacturer-orders', manufacturerId] } as any);
  };

  const shipMutation = useMutation({
    mutationFn: () => shipManufacturerOrder(manufacturerId, orderId, {
      carrier: carrier.trim() || undefined,
      trackingNumber: tracking.trim() || undefined,
    }),
    onSuccess: () => { setShipError(''); refetch(); },
    onError: (err: any) => setShipError(err.response?.data?.error || 'Failed to mark shipped'),
  });

  const registerMutation = useMutation({
    mutationFn: () => registerOrderDevices(
      manufacturerId, orderId,
      deviceIdsText.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean)
    ),
    onSuccess: () => { setRegisterError([]); setDeviceIdsText(''); refetch(); },
    onError: (err: any) => {
      const body = err.response?.data;
      setRegisterError([body?.error || 'Registration failed', ...(body?.details || [])]);
    },
  });

  const completeMutation = useMutation({
    mutationFn: () => completeManufacturerOrder(manufacturerId, orderId),
    onSuccess: () => { setRegisterError([]); refetch(); },
    onError: (err: any) => setRegisterError([err.response?.data?.error || 'Failed to complete order']),
  });

  const termsMutation = useMutation({
    mutationFn: (accept: boolean) => (accept
      ? acceptTermsOrder(manufacturerId, orderId)
      : declineTermsOrder(manufacturerId, orderId)),
    onSuccess: () => { setRegisterError([]); refetch(); },
    onError: (err: any) => setRegisterError([err.response?.data?.error || 'Failed to update the terms order']),
  });

  if (isLoading || !order) return <LoadingText>Loading order…</LoadingText>;

  const orderedUnits = order.items.reduce((s, it) => s + it.quantity, 0);
  const registered = order.registered_count ?? 0;
  const canRegister = order.status === 'paid' || order.status === 'shipped';
  const fullyRegistered = registered >= orderedUnits;

  return (
    <>
      <DetailColumns>
        <div>
          <Caption>Items</Caption>
          {order.items.map((it) => (
            <div key={it.id}>
              {it.quantity} × {it.description || it.board_display_name || it.board}
              {it.variant_sku && <MutedText> ({it.variant_sku})</MutedText>}
              <MutedText> · {formatMoney(it.unit_price_cents, order.currency)} each</MutedText>
              {it.monthly_fee_cents > 0 && (
                <MutedText> · {formatMoney(it.monthly_fee_cents, order.currency)}/mo per device</MutedText>
              )}
            </div>
          ))}
        </div>
        <div>
          <Caption>Ship to</Caption>
          {order.shipping_details ? (
            <>
              <div>{order.shipping_details.name}{order.shipping_details.company && ` · ${order.shipping_details.company}`}</div>
              <MutedText>
                {order.shipping_details.line1}{order.shipping_details.line2 && `, ${order.shipping_details.line2}`}
                <br />{order.shipping_details.city}, {order.shipping_details.state} {order.shipping_details.postal}
                {order.shipping_details.phone && <> · {order.shipping_details.phone}</>}
              </MutedText>
            </>
          ) : (
            <>
              <div>{order.shipping_name || <MutedText>—</MutedText>}</div>
              {order.shipping_address && <MutedText>{order.shipping_address}</MutedText>}
            </>
          )}
          {order.billing_details && (
            <div style={{ marginTop: 6 }}>
              <Caption>Bill to</Caption>
              <MutedText>
                {order.billing_details.name}, {order.billing_details.line1},
                {' '}{order.billing_details.city}, {order.billing_details.state} {order.billing_details.postal}
              </MutedText>
            </div>
          )}
        </div>
        <div>
          <Caption>Payment</Caption>
          <StatusChip variant={order.payment_terms === 'terms' ? 'warning' : 'blue'}>
            {order.payment_terms === 'terms' ? 'terms' : 'card'}
          </StatusChip>
          {order.payment_terms === 'terms' && order.status === 'pending' && (
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <Button size="sm" onClick={() => termsMutation.mutate(true)} disabled={termsMutation.isPending}>
                Accept terms
              </Button>
              <Button size="sm" variant="secondary" onClick={() => termsMutation.mutate(false)} disabled={termsMutation.isPending}>
                Decline
              </Button>
            </div>
          )}
        </div>
        {(order.carrier || order.tracking_number) && (
          <div>
            <Caption>Shipment</Caption>
            <div>{order.carrier || 'Carrier —'}</div>
            {order.tracking_number && <MutedText>{order.tracking_number}</MutedText>}
            {order.shipped_at && <div><MutedText>shipped {formatWhen(order.shipped_at)}</MutedText></div>}
          </div>
        )}
        {linkThrough && (
          <div>
            <Caption>Buyer</Caption>
            <ProviderLink to={`/software-providers/${order.company_id}`}>{order.company_name}</ProviderLink>
          </div>
        )}
      </DetailColumns>

      <div style={{ marginTop: 8 }}>
        <Caption>Devices — {registered} of {orderedUnits} registered</Caption>
        {order.registered_devices.length > 0 && (
          <div>
            {order.registered_devices.map((d) => (
              <MutedText key={d.device_id} style={{ marginRight: 12 }}>
                {d.device_id}{d.status === 'online' ? ' ●' : ''}
              </MutedText>
            ))}
          </div>
        )}
        {canRegister && !fullyRegistered && (
          <>
            <TextArea
              placeholder="Device IDs of the shipped units — one per line (or comma-separated)"
              value={deviceIdsText}
              onChange={(e) => setDeviceIdsText(e.target.value)}
              rows={3}
              style={{ maxWidth: 480, marginTop: 8 }}
            />
            <div style={{ marginTop: 8 }}>
              <Button size="sm" onClick={() => registerMutation.mutate()} disabled={registerMutation.isPending || !deviceIdsText.trim()}>
                {registerMutation.isPending ? 'Registering…' : 'Register devices'}
              </Button>
            </div>
            <HelperText>
              Each registered device is assigned to {order.company_name} and appears in their
              dashboard undeployed, ready for install.
            </HelperText>
          </>
        )}
        {order.status === 'shipped' && fullyRegistered && (
          <div style={{ marginTop: 8 }}>
            <Button size="sm" onClick={() => completeMutation.mutate()} disabled={completeMutation.isPending}>
              {completeMutation.isPending ? 'Completing…' : 'Complete order'}
            </Button>
          </div>
        )}
      </div>

      {registerError.length > 0 && (
        <ErrorAlert as="div">
          {registerError.map((line, i) => <div key={i}>{line}</div>)}
        </ErrorAlert>
      )}

      {shipError && <ErrorAlert>{shipError}</ErrorAlert>}

      {order.status === 'paid' && (
        <ShipForm>
          <Input
            placeholder="Carrier (UPS, FedEx…)"
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
            style={{ maxWidth: 180 }}
          />
          <Input
            placeholder="Tracking number"
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            style={{ maxWidth: 260 }}
          />
          <Button size="sm" onClick={() => shipMutation.mutate()} disabled={shipMutation.isPending}>
            {shipMutation.isPending ? 'Saving…' : 'Mark shipped'}
          </Button>
        </ShipForm>
      )}
    </>
  );
}

export function ProviderOrders({ manufacturerId, linkThrough }: {
  manufacturerId: number;
  linkThrough: boolean;
}) {
  const [statusFilter, setStatusFilter] = useState<'' | OrderStatus>('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['manufacturer-orders', manufacturerId, statusFilter],
    queryFn: () => getManufacturerOrders(manufacturerId, { status: statusFilter || undefined, limit: 100 }),
    enabled: Number.isFinite(manufacturerId),
  });

  const orders = data?.orders ?? [];

  return (
    <>
      <SectionHeader>
        <MutedText>
          Hardware purchased through the site. Mark paid orders shipped once
          they leave your dock.
        </MutedText>
        <Row gap={2}>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as '' | OrderStatus)}>
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid — awaiting shipment</option>
            <option value="shipped">Shipped</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </Select>
        </Row>
      </SectionHeader>

      {isLoading ? (
        <LoadingText>Loading orders...</LoadingText>
      ) : orders.length === 0 ? (
        <EmptyState>
          <p>No orders{statusFilter ? ' with that status' : ' yet'}.</p>
          <MutedText>Orders appear here when software providers buy your hardware through the site.</MutedText>
        </EmptyState>
      ) : (
        <TableContainer>
          <StyledTable>
            <thead>
              <tr>
                <Th>Order</Th>
                <Th>Placed</Th>
                <Th>Buyer</Th>
                <Th>Units</Th>
                <Th>Devices</Th>
                <Th>Total</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <Fragment key={o.id}>
                  <OrderRow onClick={() => setExpandedId(expandedId === o.id ? null : o.id)}>
                    <Td><strong>{o.order_number}</strong></Td>
                    <Td><Caption>{formatWhen(o.created_at)}</Caption></Td>
                    <Td>{o.company_name}</Td>
                    <Td>{o.units}</Td>
                    <Td>
                      {(o.registered_count ?? 0) >= (o.units ?? 0)
                        ? <StatusChip variant="positive">{o.registered_count}/{o.units}</StatusChip>
                        : <MutedText>{o.registered_count ?? 0}/{o.units}</MutedText>}
                    </Td>
                    <Td>{formatMoney(o.subtotal_cents, o.currency)}</Td>
                    <Td><StatusChip variant={statusVariant(o.status)}>{o.status}</StatusChip></Td>
                  </OrderRow>
                  {expandedId === o.id && (
                    <tr>
                      <ExpandTd colSpan={7}>
                        <OrderDetail manufacturerId={manufacturerId} orderId={o.id} linkThrough={linkThrough} />
                      </ExpandTd>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </StyledTable>
        </TableContainer>
      )}
      {data && data.total > orders.length && (
        <Caption>Showing {orders.length} of {data.total}.</Caption>
      )}
    </>
  );
}
